import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CountdownTimer } from "../shared/CountdownTimer";
import { CheckCircle2, Lock, Video, Camera, Check, Play, FileText, ExternalLink, RotateCcw } from "lucide-react";
import { useChallenge } from "@/contexts/ChallengeContext";
import { DailyTask } from "@/types/challenge";
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from "@capacitor/camera";
import PhotoSuccessDialog from "@/components/challenge/PhotoSuccessDialog";
import CreatePostDialog from "@/components/posts/CreatePostDialog";
import { toast } from "sonner";

// The pill shows the stored status, and "צלם" is a masculine imperative on a
// control. The DB enum stays; only the label becomes the action noun.
const taskStatusLabel = (status: DailyTask["status"]): string =>
  status === "צלם" ? "צילום" : status;

export const DailyTasksSection = React.memo(function DailyTasksSection() {
  const navigate = useNavigate();
  const {
    progress,
    currentChallengeId,
    currentChallengeType,
    getCurrentDay,
    getCurrentDayTasks,
    markTaskAsCompleted,
    currentDayData,
    activeChallengeLoadFailed
  } = useChallenge();
  const isCourse = currentChallengeType === 'course';
  
  const [tasks, setTasks] = useState<DailyTask[]>([]);
  const [completingTask, setCompletingTask] = useState<string | null>(null);
  /**
   * Tasks the member completed during THIS visit. The ✓ pop and the "all done"
   * celebration play for these only; unconditionally, every completed check
   * re-popped each time Home mounted (animate state, never mount).
   */
  const completedThisVisitRef = useRef<Set<string>>(new Set());
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState("");
  const [capturedTaskText, setCapturedTaskText] = useState("");
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  
  const currentDay = getCurrentDay();
  const hasWatchedVideo = progress?.videosWatched[currentDay] || false;
  
  // טעינת משימות מה-Context — כולל עדכון סטטוס השלמה
  useEffect(() => {
    const dayTasks = getCurrentDayTasks();
    setTasks(dayTasks);
  }, [currentChallengeId, progress?.currentDay, progress?.tasksCompleted, currentDayData]);
  
  const toggleTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.completed || !currentDayData) return;

    // Diagnostic: capture actual dayId value to trace UUID leak root cause
    const dayId = currentDayData.id;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(dayId)) {
      console.warn('[DailyTasks] dayId is not UUID:', {
        dayId,
        dayNumber: currentDayData.dayNumber,
        currentDay,
        currentDayDataKeys: Object.keys(currentDayData),
      });
    }

    setCompletingTask(taskId);
    setTimeout(() => setCompletingTask(null), 600);

    try {
      await markTaskAsCompleted(currentDay, dayId, taskId, true);
      completedThisVisitRef.current.add(taskId);
    } catch {
      // Not saved: leave the task open rather than show a check that reverts.
      toast.error('לא הצלחנו לסמן את המשימה. בדקו את החיבור ונסו שוב.');
      return;
    }

    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, completed: true } : t
    ));
  };

  const handleCameraTask = async (task: DailyTask) => {
    try {
      const photo = await CapacitorCamera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        quality: 80,
      });
      if (photo.dataUrl) {
        setCapturedPhotoUrl(photo.dataUrl);
        setCapturedTaskText(task.text);
        setPhotoDialogOpen(true);
        // סימון המשימה כבוצעה ברקע
        toggleTask(task.id);
      }
    } catch {
      // המשתמש ביטל - לא קורה כלום
    }
  };

  const handlePublishToFeed = () => {
    setPhotoDialogOpen(false);
    setPublishDialogOpen(true);
  };

  const handleActionClick = (task: DailyTask) => {
    if (task.status === "צפייה") {
      const taskParam = task.isMainVideo ? "" : `?taskId=${task.id}`;
      navigate(`/challenge/${currentChallengeId}/day/${currentDay}${taskParam}`);
      return;
    }
    
    if (task.completed) return;
    
    if (task.status === "צלם") {
      handleCameraTask(task);
    } else if (task.status === "בוצע") {
      toggleTask(task.id);
    }
  };

  const completedTasks = tasks.filter(t => t.completed).length;
  const allCompleted = tasks.length > 0 && completedTasks === tasks.length;

  // אם אין אתגר פעיל
  if (!currentChallengeId || tasks.length === 0) {
    return (
      <section className="px-0 py-4">
        <h2 className="text-lg font-semibold text-foreground text-right mb-4">
          המשימות היומיות
        </h2>
        <div className="bg-card rounded-2xl p-6 shadow-sm flex flex-col items-center text-center gap-3">
          <div className="h-12 w-12 rounded-full bg-muted grid place-items-center">
            <Check className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">
            {/* The retry lives in "הפעילות שלי" above — it reloads the same data. */}
            {activeChallengeLoadFailed
              ? "לא הצלחנו לטעון את המשימות של היום."
              : "הצטרפו לאתגר או קורס כדי לקבל משימות יומיות"}
          </p>
        </div>
      </section>
    );
  }

  const getTaskIcon = (task: DailyTask) => {
    if (task.completed) {
      return (
        <CheckCircle2
          className={`h-5 w-5 text-success ${completedThisVisitRef.current.has(task.id) ? 'animate-scale-in' : ''}`}
        />
      );
    }

    const isLocked = task.requiresVideoWatch && !hasWatchedVideo;
    if (isLocked) {
      return <Lock className="h-5 w-5 text-muted-foreground" />;
    }

    if (task.status === "צפייה") {
      return <Video className="h-5 w-5 text-primary" />;
    } else if (task.status === "צלם") {
      return <Camera className="h-5 w-5 text-primary" />;
    } else {
      return <Check className="h-5 w-5 text-primary" />;
    }
  };

  return (
    <section className="px-0 py-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">המשימות היומיות</h2>
        <CountdownTimer />
      </div>
      
      <div className="bg-card rounded-2xl p-4 md:p-6 shadow-sm">
        {allCompleted ? (
          // Empty state - כל המשימות הושלמו
          <div className={`text-center py-8 ${completedThisVisitRef.current.size > 0 ? 'animate-fade-in' : ''}`}>
            <div className="flex justify-center mb-4">
              <CheckCircle2 className={`h-16 w-16 text-success ${completedThisVisitRef.current.size > 0 ? 'animate-scale-in' : ''}`} />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">מצוין! 🎊</h3>
            <p className="text-sm text-muted-foreground">השלמת את כל המשימות היום</p>
            <p className="text-xs text-muted-foreground mt-2">נתראה מחר למשימות חדשות</p>
            <button
              onClick={() => navigate(`/challenge/${currentChallengeId}/day/${currentDay}`)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary text-sm text-foreground active:bg-secondary/80 transition-colors"
            >
              <Play className="h-4 w-4" />
              צפייה חוזרת בסרטון היומי
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.map((task, index) => {
              const isLocked = task.requiresVideoWatch && !hasWatchedVideo;
              
              return (
            <React.Fragment key={task.id}>
              <div 
                className={`self-stretch flex justify-between items-center gap-2 transition-all duration-300 ${
                  completingTask === task.id ? 'animate-scale-in' : ''
                }`}
              >
                {/* אייקון */}
                <div className={`p-1.5 rounded-lg flex justify-start items-center gap-1 flex-shrink-0 ${
                  isLocked ? 'bg-muted' : 'bg-primary/10'
                } ${task.completed ? 'opacity-60' : ''}`}>
                  <div 
                    className={`size-5 relative`}
                    aria-label={task.completed ? "משימה הושלמה" : isLocked ? "משימה נעולה" : "סימון כהושלם"}
                  >
                    {getTaskIcon(task)}
                  </div>
                </div>
                
                {/* טקסט ומד התקדמות */}
                 <div className={`flex-1 flex flex-col justify-start items-end gap-1 ${task.completed ? 'opacity-60' : ''}`}>
                   <div className="self-stretch text-right">
                     <div className={`text-base font-normal leading-none transition-all duration-300 ${
                       task.completed ? 'text-muted-foreground line-through' : isLocked ? 'text-muted-foreground' : 'text-foreground'
                     }`}>
                       {task.text}
                     </div>
                     {/* The row used to fade to 40% with no reason given. It says what
                         opens it instead, and stays readable. */}
                     {isLocked && (
                       <div className="mt-1.5 text-xs text-muted-foreground">
                         ייפתח אחרי הצפייה בסרטון היומי
                       </div>
                     )}
                     {task.fileUrl && !isLocked && (
                       <a
                         href={task.fileUrl}
                         target="_blank"
                         rel="noopener noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         className="tap-target z-10 mt-2 inline-flex w-fit items-center gap-1 text-xs text-primary active:underline"
                       >
                         <FileText className="h-3.5 w-3.5" />
                         {task.fileName || "פתיחת קובץ"}
                         <ExternalLink className="h-3 w-3" />
                       </a>
                     )}
                   </div>
                  <div className="w-full h-4 relative">
                    <div className="w-full h-4 bg-muted rounded-sm overflow-hidden">
                      <div 
                        className={`h-full bg-primary rounded-sm transition-all duration-500 ${
                          completingTask === task.id ? 'animate-pulse' : ''
                        }`}
                        style={{ width: task.completed ? "100%" : "0%" }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* כפתור צפייה/בוצע/צלם */}
                {task.status === "צפייה" && task.completed ? (
                  // Watched: a quiet text link, not the full-strength "צפייה" pill,
                  // so a finished lesson stops looking like a to-do.
                  <button
                    onClick={() => handleActionClick(task)}
                    className="tap-target flex-shrink-0 flex items-center gap-1 min-h-8 text-xs font-medium leading-none whitespace-nowrap text-muted-foreground active:opacity-70 transition-opacity"
                    aria-label={`צפייה חוזרת - ${task.text}`}
                  >
                    <RotateCcw className="h-3 w-3" aria-hidden="true" />
                    צפייה חוזרת
                  </button>
                ) : (
                <button
                  onClick={() => handleActionClick(task)}
                  className={`tap-target px-3 py-1 rounded-md shadow-sm flex justify-center items-center gap-2 disabled:cursor-not-allowed flex-shrink-0 ${
                    isLocked ? 'bg-muted' : 'bg-card'
                  } ${task.completed ? 'opacity-60' : ''}`}
                  disabled={task.status !== "צפייה" && (task.completed || isLocked)}
                  aria-label={`${taskStatusLabel(task.status)} - ${task.text}${isLocked ? ' (ייפתח אחרי הצפייה בסרטון היומי)' : ''}`}
                >
                  <div className={`justify-start text-xs font-medium leading-none whitespace-nowrap flex items-center gap-1 ${
                    isLocked ? 'text-muted-foreground' : 'text-primary'
                  }`}>
                    {isLocked && <Lock className="h-3 w-3" />}
                    {taskStatusLabel(task.status)}
                  </div>
                </button>
                )}
              </div>
              
              {/* מפריד בין משימות (לא אחרי האחרונה) */}
              {index < tasks.length - 1 && (
                <div 
                  className="my-4 border-t border-border/40" 
                  style={{ 
                    borderStyle: 'dashed',
                    borderWidth: '1px 0 0 0',
                    borderSpacing: '10px'
                  }}
                />
              )}
            </React.Fragment>
          );
            })}
          </div>
        )}
      </div>

      <PhotoSuccessDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onPublish={handlePublishToFeed}
        imageUrl={capturedPhotoUrl}
        hidePublish={isCourse}
      />

      <CreatePostDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        initialContent={capturedTaskText}
        initialMedia={capturedPhotoUrl ? [{ type: "image" as const, url: capturedPhotoUrl, blob: null }] : undefined}
      />
    </section>
  );
});
